// src/app/modules/Members/pages/Members.jsx
import React, { useEffect, useState, useRef } from "react";
import { shallowEqual, useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as actions from "../../../Dashbord/_redux/dashboard/Actions";
import { useOrgConfig } from "../../../../../hooks/useOrgConfig";
import { MembersTable } from "../database";
import { InviteMembersModal } from "../components/InviteMembersModal";
import { EditMemberModal } from "../components/EditMemberModal";
import { ConfirmActionModal } from '../../../../components/ConfirmActionModal';
import './members.scss';

import {
  Users, UserPlus, Shield, UserCheck, UserX,
  Filter, Search, ShieldCheck, Home,
} from 'lucide-react';

export function Members() {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const isMountedRef = useRef(true);
  const { getLabel, hasFeature, org } = useOrgConfig();

  const { members, membersMeta, memberStats, loading } = useSelector(
    (state) => ({
      members:     state.dashboard.members,
      membersMeta: state.dashboard.membersMeta,
      memberStats: state.dashboard.memberStats,
      loading:     state.dashboard.listLoading,
    }),
    shallowEqual
  );

  const [currentPage,       setCurrentPage]       = useState(1);
  const [perPage]                                  = useState(10);
  const [searchTerm,        setSearchTerm]        = useState('');
  const [roleFilter,        setRoleFilter]        = useState('');
  const [statusFilter,      setStatusFilter]      = useState('active');
  const [memberTypeFilter,  setMemberTypeFilter]  = useState('');

  const [showInviteModal,   setShowInviteModal]   = useState(false);
  const [showEditModal,     setShowEditModal]     = useState(false);
  const [selectedMember,    setSelectedMember]    = useState(null);
  const [showResendModal,   setShowResendModal]   = useState(false);
  const [showCancelModal,   setShowCancelModal]   = useState(false);
  const [showSuspendModal,  setShowSuspendModal]  = useState(false);
  const [showDeleteModal,   setShowDeleteModal]   = useState(false);
  const [selectedMemberId,  setSelectedMemberId]  = useState(null);
  const [selectedMemberEmail, setSelectedMemberEmail] = useState(null);
  const [actionLoading,     setActionLoading]     = useState(false);

  useEffect(() => { isMountedRef.current = true; return () => { isMountedRef.current = false; }; }, []);
  useEffect(() => { init_functions(); }, []);
  useEffect(() => { if (org?.organization_id) fetchMembers(); }, [currentPage, searchTerm, roleFilter, statusFilter, memberTypeFilter]);

  function init_functions() {
    if (org?.organization_id && isMountedRef.current) { fetchMembers(); fetchStats(); }
  }

  function fetchMembers() {
    if (!isMountedRef.current) return;
    const params = { page: currentPage, limit: perPage, organization_id: org.organization_id };
    if (searchTerm)      params.search      = searchTerm;
    if (roleFilter)      params.role        = roleFilter;
    if (statusFilter)    params.status      = statusFilter;
    if (memberTypeFilter) params.member_type = memberTypeFilter;
    dispatch(actions.fetchAllData('members', org.organization_id, params));
  }

  function fetchStats() {
    if (!isMountedRef.current) return;
    dispatch(actions.fetchAllData('member_stats', org.organization_id));
  }

  function handleSearch() { setCurrentPage(1); fetchMembers(); }
  function handleViewDetails(id) { navigate(`/members/view/${id}`); }
  function handleEdit(member) { setSelectedMember(member); setShowEditModal(true); }
  function handleReactivate(id) {
    dispatch(actions.addData('reactivate_member', { membership_id: id })).then(() => { fetchMembers(); fetchStats(); });
  }
  function handleSuspend(id) { setSelectedMember(members.find(m => m.membership_id === id)); setShowSuspendModal(true); }
  function handleDelete(id)  { setSelectedMember(members.find(m => m.membership_id === id)); setShowDeleteModal(true); }
  function handleResendInvite(id, email) {
    const m = members.find(m => m.membership_id === id);
    setSelectedMemberId(id); setSelectedMemberEmail(m?.invite_email || email); setShowResendModal(true);
  }
  function handleCancelInvite(id, email) {
    const m = members.find(m => m.membership_id === id);
    setSelectedMemberId(id); setSelectedMemberEmail(m?.invite_email || email); setShowCancelModal(true);
  }

  async function handleResendInviteConfirm() {
    setActionLoading(true);
    try { await dispatch(actions.addData('resend_invite', { membership_id: selectedMemberId, organization_id: org?.organization_id })); fetchMembers(); setShowResendModal(false); }
    catch (e) { console.error(e); } finally { setActionLoading(false); }
  }
  async function handleCancelInviteConfirm() {
    setActionLoading(true);
    try { await dispatch(actions.addData('cancel_invite', { membership_id: selectedMemberId, organization_id: org?.organization_id })); fetchMembers(); fetchStats(); setShowCancelModal(false); }
    catch (e) { console.error(e); } finally { setActionLoading(false); }
  }
  async function handleSuspendConfirm() {
    setActionLoading(true);
    try { await dispatch(actions.addData('suspend_member', { membership_id: selectedMember.membership_id })); fetchMembers(); fetchStats(); setShowSuspendModal(false); }
    catch (e) { console.error(e); } finally { setActionLoading(false); }
  }
  async function handleDeleteConfirm() {
    setActionLoading(true);
    try { await dispatch(actions.addData('delete_member', { membership_id: selectedMember.membership_id, organization_id: org?.organization_id })); fetchMembers(); fetchStats(); setShowDeleteModal(false); }
    catch (e) { console.error(e); } finally { setActionLoading(false); }
  }

  const stats = memberStats || { total_members: 0, active_members: 0, suspended_members: 0, pending_invites: 0, staff_count: 0, resident_count: 0 };

  const statCards = [
    { icon: <Users size={20} />,     bg: '#F0EEFF', color: '#5341C4', label: 'Total Members',   value: stats.total_members },
    { icon: <UserCheck size={20} />, bg: '#F0FDF4', color: '#16a34a', label: 'Active',           value: stats.active_members },
    ...(hasFeature('hasStaff') ? [{ icon: <ShieldCheck size={20} />, bg: '#EFF6FF', color: '#2563EB', label: getLabel('staff'), value: stats.staff_count || 0 }] : []),
    ...(hasFeature('hasResidents') ? [{ icon: <Home size={20} />,   bg: '#F0EEFF', color: '#5341C4', label: getLabel('residents'), value: stats.resident_count || 0 }] : []),
    { icon: <UserX size={20} />,     bg: '#FFFBEB', color: '#D97706', label: 'Pending Invites',  value: stats.pending_invites },
  ];

  return (
    <>
      <div className="members-page">

        {/* ── Header ── */}
        <div className="mb-header">
          <div className="mb-header__left">
            <h1>{getLabel('membersTitle')}</h1>
            <p>{org?.name} · Manage {getLabel('organizationMembers')}</p>
          </div>
          <button className="mb-btn mb-btn--primary" onClick={() => setShowInviteModal(true)}>
            <UserPlus size={13} />Invite Members
          </button>
        </div>

        {/* ── Stat cards ── */}
        <div className="mb-stats">
          {statCards.map((s, i) => (
            <div key={i} className="mb-stat">
              <div className="mb-stat__icon" style={{ background: s.bg, color: s.color }}>{s.icon}</div>
              <div>
                <div className="mb-stat__label">{s.label}</div>
                <div className="mb-stat__value">{s.value}</div>
              </div>
            </div>
          ))}
        </div>

        {/* ── Filters ── */}
        <div className="mb-card">
          <div className="mb-card__body" style={{ padding: '11px 16px' }}>
            <div className="mb-filters">
              <div className="mb-search">
                <Search size={14} />
                <input
                  placeholder="Search by name, email, or phone…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
              </div>

              <select className="mb-select" value={roleFilter} onChange={(e) => { setRoleFilter(e.target.value); setCurrentPage(1); }}>
                <option value="">All Roles</option>
                <option value="admin">Admin</option>
                <option value="manager">Manager</option>
                <option value="security">Security</option>
                <option value="staff">Staff</option>
                <option value="member">Member</option>
              </select>

              <select className="mb-select" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}>
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="pending">Pending</option>
              </select>

              <button className="mb-apply-btn" onClick={handleSearch}>
                <Filter size={13} />Apply
              </button>
            </div>
          </div>
        </div>

        {/* ── Table ── */}
        <div className="mb-card">
          <div className="mb-card__header">
            <div className="mb-card__title"><Users size={14} />Team Members</div>
            <span className="mb-card__meta">{membersMeta?.totalResults || 0} total members</span>
          </div>
          <div className="mb-table-wrap">
            <MembersTable
              members={members || []}
              loading={loading}
              onViewDetails={handleViewDetails}
              onEdit={handleEdit}
              onSuspend={handleSuspend}
              onReactivate={handleReactivate}
              onDelete={handleDelete}
              onCancelInvite={handleCancelInvite}
              onResendInvite={handleResendInvite}
              currentPage={currentPage}
              perPage={perPage}
              totalCount={membersMeta?.totalResults || 0}
              onPageChange={setCurrentPage}
              getLabel={getLabel}
              hasFeature={hasFeature}
            />
          </div>
        </div>

      </div>

      {/* ── Modals ── */}
      <InviteMembersModal show={showInviteModal} onHide={() => setShowInviteModal(false)} organizationId={org?.organization_id}
        onSuccess={() => { setCurrentPage(1); fetchMembers(); fetchStats(); }} />
      <EditMemberModal show={showEditModal} onHide={() => { setShowEditModal(false); setSelectedMember(null); }} member={selectedMember}
        onSuccess={() => { setCurrentPage(1); fetchMembers(); fetchStats(); }} />
      <ConfirmActionModal show={showResendModal} onHide={() => setShowResendModal(false)} onConfirm={handleResendInviteConfirm}
        title="Resend Invitation" message={<div><p>Resend invitation to <strong>{selectedMemberEmail}</strong>?</p><p className="mt-2 text-muted">A new invitation link will be sent.</p></div>}
        confirmText="Resend Invitation" type="info" loading={actionLoading} />
      <ConfirmActionModal show={showCancelModal} onHide={() => setShowCancelModal(false)} onConfirm={handleCancelInviteConfirm}
        title="Cancel Invitation" message={<div><p>Cancel invitation for <strong>{selectedMemberEmail}</strong>?</p><p className="mt-2 text-danger">This will permanently remove the pending invitation.</p></div>}
        confirmText="Cancel Invitation" type="danger" loading={actionLoading} />
      <ConfirmActionModal show={showSuspendModal} onHide={() => setShowSuspendModal(false)} onConfirm={handleSuspendConfirm}
        title="Suspend Member" message={<div><p>Suspend <strong>{selectedMember?.user?.first_name} {selectedMember?.user?.last_name}</strong>?</p><p className="mt-2">They can be reactivated later.</p></div>}
        confirmText="Suspend Member" type="warning" loading={actionLoading} />
      <ConfirmActionModal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} onConfirm={handleDeleteConfirm}
        title="Remove Member" message={<div><p>Remove <strong>{selectedMember?.user?.first_name} {selectedMember?.user?.last_name}</strong>?</p><p className="mt-2 text-danger"><strong>This action cannot be undone.</strong></p></div>}
        confirmText="Remove Member" type="danger" loading={actionLoading} />
    </>
  );
}